import React, {
  ChangeEvent,
  ComponentProps,
  useEffect,
  useRef,
  useState,
} from 'react';
import { AutoSizeTextArea } from 'shared/components/ui/auto-size-text-area';
import { TextAreaPopupMenu } from 'shared/components/ui/url-parser/text-area-popup-menu';
import { UrlParser } from 'shared/components/ui/url-parser/url-parser';
import { useContextMenuState } from 'shared/hooks/use-context-menu-state';

export type UrlParserTextAreaProps = Omit<
  ComponentProps<typeof AutoSizeTextArea>,
  'onChange'
> &
  Omit<
    ComponentProps<typeof UrlParser>,
    'value' | 'onClick' | 'onContextMenu' | 'onChange' | 'onBlur' | 'onFocus'
  > & {
    onChange: (value: string) => void;
  };

export const UrlParserTextArea: React.FC<UrlParserTextAreaProps> = ({
  value,
  onChange,
  disabled,
  onFocus,
  onBlur,
  respectNewLines,
  linkComponent,
  ...rest
}) => {
  const focussedRef = useRef(false);
  const [tempValue, setTempValue] = useState(value ?? '');
  const [displayTextArea, setDisplayTextArea] = useState(false);

  const {
    open: openMenu,
    close: closeMenu,
    position: menuPosition,
  } = useContextMenuState();

  const showTextArea = () => setDisplayTextArea(true);

  const _onEdit = () => {
    focussedRef.current = true;
    setDisplayTextArea(true);
  };

  const _onDelete = () => {
    onChange('');
    setDisplayTextArea(false);
    focussedRef.current = false;
  };

  const _onFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    _onEdit();
    onFocus?.(e);
  };

  const _onBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    focussedRef.current = false;
    setDisplayTextArea(false);
    onBlur?.(e);
  };

  const _onChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    if (disabled) {
      return;
    }

    setTempValue(e.target.value);
    onChange(e.target.value);
  };

  useEffect(() => {
    if (!focussedRef.current) {
      setTempValue(value ?? '');
    }
  }, [value]);

  return (
    <>
      {displayTextArea || !tempValue ? (
        <AutoSizeTextArea
          {...rest}
          value={tempValue}
          onChange={_onChange}
          onBlur={_onBlur}
          onFocus={_onFocus}
          disabled={disabled}
          focusOnMount={!!value}
        />
      ) : (
        <UrlParser
          {...rest}
          onClick={showTextArea}
          value={tempValue}
          onContextMenu={openMenu}
          respectNewLines={respectNewLines}
          linkComponent={linkComponent}
        />
      )}

      {menuPosition && (
        <TextAreaPopupMenu
          onClose={closeMenu}
          onEdit={_onEdit}
          onDelete={_onDelete}
          location={menuPosition}
        />
      )}
    </>
  );
};
